import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { useGetAllProgramIndicationsQuery } from '../../../types';
import {
  DEFAULT_ICD_CODE_FORM_VALUES,
  IcdCodeFormValues,
  IcdCodeValidationSchema,
} from './validation-schema';

interface IcdCodeFormProps {
  onSubmit: (values: IcdCodeFormValues) => void | Promise<void>;
  initialValues?: IcdCodeFormValues;
}

export const ICD_CODE_FORM_INDICATION_TEST_ID =
  'icd-code-form-indication-input';
export const ICD_CODE_FORM_UNIVERSAL_CODE_TEST_ID =
  'icd-code-form-universal-code-input';
export const ICD_CODE_FORM_RIGHT_CODE_TEST_ID =
  'icd-code-form-right-code-input';
export const ICD_CODE_FORM_LEFT_CODE_TEST_ID = 'icd-code-form-left-code-input';
export const ICD_CODE_FORM_START_DATE_TEST_ID =
  'icd-code-form-start-date-input';
export const ICD_CODE_FORM_END_DATE_TEST_ID = 'icd-code-form-end-date-input';
export const ICD_CODE_FORM_SUBMIT_BUTTON_TEST_ID =
  'icd-code-form-submit-button';
export const ICD_CODE_FORM_CANCEL_BUTTON_TEST_ID =
  'icd-code-form-cancel-button';

export const IcdCodeForm = ({
  onSubmit,
  initialValues,
}: IcdCodeFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();

  const { data: programIndicationsData, loading: indicationsLoading } =
    useGetAllProgramIndicationsQuery();

  const names = new Set(
    programIndicationsData?.getProgramIndicationsById.map(
      pi => pi.indicationName,
    ) ?? [],
  );
  const indicationOptions = [...names].sort((a, b) => a.localeCompare(b));

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
  } = useForm<IcdCodeFormValues>({
    mode: 'onChange',
    resolver: yupResolver(IcdCodeValidationSchema),
    defaultValues: DEFAULT_ICD_CODE_FORM_VALUES,
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (values: IcdCodeFormValues): Promise<void> => {
    await onSubmit(values);
  };

  const isEditMode = !!initialValues;

  return (
    <form>
      <HHStack spacing={6} direction="column">
        <Controller
          control={control}
          name="indication"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              value={value || null}
              options={indicationOptions}
              isOptionEqualToValue={(option, val): boolean => option === val}
              onChange={(_, newValue): void => onChange(newValue ?? '')}
              disabled={isEditMode}
              loading={indicationsLoading}
              freeSolo={isEditMode}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    role: 'combobox',
                    name: 'indication',
                  }}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label="Indication"
                  error={!!errors.indication}
                  helperText={errors.indication?.message}
                  data-testid={ICD_CODE_FORM_INDICATION_TEST_ID}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="universalCode"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Universal Code"
              value={value}
              onChange={onChange}
              error={!!errors.universalCode}
              helperText={errors.universalCode?.message}
              disabled={isEditMode}
              inputProps={{ role: 'textbox', name: 'universalCode' }}
              data-testid={ICD_CODE_FORM_UNIVERSAL_CODE_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="rightCode"
          render={({
            field: { onChange, value },
            fieldState: { error },
          }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Right Code"
              value={value}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
              inputProps={{ role: 'textbox', name: 'rightCode' }}
              data-testid={ICD_CODE_FORM_RIGHT_CODE_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="leftCode"
          render={({
            field: { onChange, value },
            fieldState: { error },
          }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Left Code"
              value={value}
              onChange={onChange}
              error={!!error}
              helperText={error?.message}
              inputProps={{ role: 'textbox', name: 'leftCode' }}
              data-testid={ICD_CODE_FORM_LEFT_CODE_TEST_ID}
            />
          )}
        />

        <HHStack direction={{ xs: 'column', md: 'row' }} spacing={6}>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="Start Date"
                value={value}
                onChange={(newValue): void => {
                  onChange(newValue ? new Date(newValue) : null);
                }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    fullWidth
                    hhVariant="variant-bypass"
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                    data-testid={ICD_CODE_FORM_START_DATE_TEST_ID}
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="End Date"
                value={value}
                onChange={(newValue): void => {
                  onChange(newValue ? new Date(newValue) : null);
                }}
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    fullWidth
                    hhVariant="variant-bypass"
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                    data-testid={ICD_CODE_FORM_END_DATE_TEST_ID}
                  />
                )}
              />
            )}
          />
        </HHStack>

        <HHStack direction="row" spacing={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="text"
            onClick={handleCancel}
            type="button"
            disabled={isSubmitting}
            data-testid={ICD_CODE_FORM_CANCEL_BUTTON_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit(handleFormSubmit)}
            data-testid={ICD_CODE_FORM_SUBMIT_BUTTON_TEST_ID}
          >
            Submit
          </HHLoadingButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
